import { CommonModule } from "@angular/common";
import {
    Component, forwardRef, ViewEncapsulation,
    ChangeDetectionStrategy, model, input,
    effect, ChangeDetectorRef, output,
    ElementRef, Inject, Optional, SkipSelf,
    contentChild,
    TemplateRef
} from "@angular/core";
import { ControlContainer, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { V_COMPONENT_CONTAINER, V_FORM_CONTROL_TOKEN, WRAPPER_TOKEN } from "../wrapper.tokens";
import { StepperConfig } from "./stepper.config";
import { FormWrapper, VContainerComponent } from "../wrapper.interface";
import { StepperModule } from 'primeng/stepper';
import { Step } from "./stepper.interface";
import { Router } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { VButtonComponent } from "../button/button.component";
import { GcLayoutModule } from "src/app/util/defaultModules";
import { VIconComponent } from "src/app/components/v-icon/v-icon.component";
import { Color, PrimeColor } from "../util";
import { VVarDirective } from "src/app/components/v-var/v-var.directive";


const DEFAULT_STEP_COLOR: StepColor = {
    text:       '--v-text-color',
    background: 'primary'
};

const FUTURE_STEP_COLOR: StepColor = {
    text:       '--v-text-color',
    background: 'secondary'
};

const INVALID_STEP_COLOR: StepColor = {
    text:       '--v-text-color',
    background: 'danger'
};

const VALID_STEP_COLOR: StepColor = {
    text:       '--v-text-color',
    background: 'success'
};


@Component({
    selector:    'v-stepper',
    templateUrl: './stepper.component.haml',
    styleUrls:   ['./stepper.component.sass'],
    providers:   [
        { provide: V_FORM_CONTROL_TOKEN, useExisting: forwardRef(() => VStepperComponent) },
        { provide: WRAPPER_TOKEN, useExisting: forwardRef(() => VStepperComponent) },
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => VStepperComponent), multi: true }
    ],
    encapsulation:   ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone:      true,
    imports:         [
        CommonModule,   ReactiveFormsModule,
        StepperModule,  VButtonComponent,
        GcLayoutModule, VVarDirective,
        VIconComponent, TranslateModule,
    ]
})
export class VStepperComponent extends FormWrapper<StepperConfig, number> {
    protected _baseConfig: StepperConfig = StepperConfig.baseTheme.Default;
    public  valueSignal = model<number>(undefined, {alias: 'value'});
    public readonly valueChange = output<number>();
    public readonly steps = input.required<Step[]>();

    private readonly onEvents = toSignal(this.router.events);
    public get value(): number {
        // need to use value to stay compatible with FormWrapper
        return this.valueSignal();
    }
    public set value(value: number) {
        if ([undefined, null].includes(value) || value < 0 || value >= this.steps().length || value === this.valueSignal()) return;
        this.valueSignal.set(value);
    }

    public readonly labelRef = contentChild<TemplateRef<unknown>>('label');
    public readonly buttonRef = contentChild<TemplateRef<unknown>>('button');

    constructor(
        private readonly router: Router,
        private readonly cd: ChangeDetectorRef,
        private readonly el: ElementRef,
        @Optional() protected controlContainer: ControlContainer,
        @Inject(V_COMPONENT_CONTAINER) @Optional() @SkipSelf() protected parent?: VContainerComponent
    ) {
        super(controlContainer, parent);
        effect(() => { // TODO: @Rene brauchen wird das noch? 
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _event = this.onEvents();
            this.cd.markForCheck();
        });
        effect(() => {
            const value = this.valueSignal();
            this.valueChange.emit(value);
            this.cd.markForCheck();
            this.scrollTo(value);
        });
    }

    private scrollTo(index: number) {
        const nodes: NodeList = this.el.nativeElement.querySelectorAll('.p-steps-item');
        (nodes.item(index) as HTMLElement)?.scrollIntoView?.();
    }

    public isStepActive(index: number) {
        return index === (this.valueSignal() ?? 0);
    }

    public isStepClickable(index: number) {
        if (this.readonly()) return false;
        const steps = this.steps();
        if (!steps || steps.length === 0) return false;

        const previousSteps = steps.slice(0, index);
        // /**
        //  * Allow traversal to all steps if all previous steps have a value in `valid`.
        //  * - Unset value means the step was not visited before.
        //  * - A step can become invalid if it was visited before but the validation changed or new data is missing.
        //  *   Might happen when restoring a draft
        //  */
        // if (previousSteps.some(step => [null, undefined].includes(step.valid))) return false;

        // Alternatively block traversal to steps if any previous step is invalid.
        if (previousSteps.some(step => !step.valid)) return false;

        return index < steps.length && !steps[index].disabled;
    }

    public clickStep(index: number, callback: Function) {
        const step = this.steps()?.[index];
        if (!step || !this.isStepClickable(index)) return;
        callback();
    }

    public getStepColor(index: number): StepColor {
        const step = this.steps()?.[index];
        if (!step || this.isStepActive(index))
            return DEFAULT_STEP_COLOR;

        if (index > (this.valueSignal() ?? 0))
            return FUTURE_STEP_COLOR;

        if (step.valid === false)
            return INVALID_STEP_COLOR;

        return VALID_STEP_COLOR;
    }

}

interface StepColor {
    text: Color;
    background: PrimeColor;
}